import React, { useState, useMemo, useEffect } from 'react';
import { useServicesStore } from '../store/stores';
import { sendCommandToDefault } from '../socket';
import { dialogEvents } from '../component';
import { DataTable } from '../component/table/dataTable';
import { Button, ButtonGroup } from '../component/button';
import { Loader } from '../component/loader';
import { PageContent } from '../component/pageContent';
import { serviceStore } from '../store';
import { Input } from '../component/input';
import styled from '../styles/styled-components';

class ServicesDataTable extends DataTable<ServiceInfo> {}

const startService = (servicename: string) => {
  sendCommandToDefault('StartService', servicename);
  serviceStore.markServiceWithActionInProgress(servicename, 'start');
};

const stopService = (servicename: string) => {
  sendCommandToDefault('StopService', servicename);
  serviceStore.markServiceWithActionInProgress(servicename, 'stop');
};

const rebootService = (servicename: string) => {
  sendCommandToDefault('restartService', servicename);
  serviceStore.markServiceWithActionInProgress(servicename, 'restart');
};

const confirmStart = (servicename: string, serviceId: string) => {
  dialogEvents.dialog({
    title: 'Confirm start service',
    body: <p>Are you sure you want to start service {servicename}?</p>,
    buttons: [
      <Button
        key="yes"
        primary
        onClick={() => startService(serviceId)}
        content="Yes"
      />,
      <Button key="no" content="No" onClick={() => {}} />,
    ],
  });
};

const confirmReboot = (servicename: string, serviceId: string) => {
  dialogEvents.dialog({
    title: 'Confirm restart service',
    body: <p>Are you sure you want to restart the service {servicename}?</p>,
    buttons: [
      <Button
        key="yes"
        primary
        onClick={() => rebootService(serviceId)}
        content="Yes"
      />,
      <Button key="no" content="No" onClick={() => {}} />,
    ],
  });
};

const confirmStop = (servicename: string, serviceId: string) => {
  dialogEvents.dialog({
    title: 'Confirm stop service',
    body: <p>Are you sure you want to stop service {servicename}?</p>,
    buttons: [
      <Button
        key="yes"
        primary
        onClick={() => stopService(serviceId)}
        content="Yes"
      />,
      <Button key="no" content="No" onClick={() => {}} />,
    ],
  });
};

const ServicesTable = ({ data, ...rest }) => (
  <ServicesDataTable
    divided
    sortable
    initialSortField={t => t.displayName}
    initialSortOrder="asc"
    dataKey="serviceName"
    data={data}
    columns={[
      {
        header: 'Display Name',
        render: t => t.displayName,
        sortField: t => t.displayName,
      },
      // {
      //   header: 'Service name',
      //   render: t => t.serviceName,
      //   sortField: t => t.serviceName,
      //   // ellipsis: '20px',
      // },
      { header: 'Status', render: t => t.status, sortField: t => t.status },
      {
        header: 'Startup Type',
        render: t => t.startupType,
        sortField: t => t.startupType,
      },
      { header: 'Logon As', render: t => t.logonAs, sortField: t => t.logonAs },
      {
        header: '',
        key: 'start',
        render: t => (
          <ButtonGroup>
            <Button
              key="Start"
              icon="play"
              size="small"
              disabled={!!t.actionInProgress || t.status !== 'Stopped'}
              loading={t.actionInProgress === 'start'}
              onClick={() => confirmStart(t.displayName, t.serviceName)}
            />
            <Button
              key="Stop"
              size="small"
              disabled={!!t.actionInProgress || t.status !== 'Running'}
              loading={t.actionInProgress === 'stop'}
              onClick={() => confirmStop(t.displayName, t.serviceName)}
              icon="square"
            />
            <Button
              key="Restart"
              size="small"
              disabled={!!t.actionInProgress || t.status !== 'Running'}
              loading={t.actionInProgress === 'restart'}
              onClick={() => confirmReboot(t.displayName, t.serviceName)}
              icon="refreshCw"
            />
          </ButtonGroup>
        ),
      },
    ]}
    {...rest}
  />
);

const MemoizedTable = React.memo(ServicesTable);

const useUpdateServiceInfoEffect = (ms: number) =>
  useEffect(() => {
    const interval = setInterval(
      () => sendCommandToDefault('requestServiceInfoPatches'),
      ms,
    );
    return () => clearInterval(interval);
  });

const ServiceListImpl = ({ data, ...rest }: { data: ServiceInfo[] }) => {
  const [filterInputValue, setFilterInputValue] = useState('');
  const [filterValue, setFilterValue] = useState('');

  useUpdateServiceInfoEffect(3000);

  const filteredServices = useMemo(
    () => {
      if (!filterValue) return data;

      console.time('filteredServices');
      const newData = data.filter(
        serviceInfo =>
          serviceInfo.serviceName
            .toLowerCase()
            .indexOf(filterValue.toLowerCase()) > -1 ||
          serviceInfo.displayName
            .toLowerCase()
            .indexOf(filterValue.toLowerCase()) > -1,
      );
      console.timeEnd('filteredServices');
      return newData;
    },
    [filterValue, data],
  );

  return (
    <>
      <Form
        onSubmit={_e => {
          _e.preventDefault();
          setFilterValue(filterInputValue);
        }}
      >
        <Input
          value={filterInputValue}
          onChange={(_e, v) => setFilterInputValue(v)}
          placeholder="Type service name to filter..."
        />
        <Button
          icon="search"
          content="Filter"
          type="submit"
          primary
          onClick={_e => setFilterValue(filterInputValue)}
        />
      </Form>
      <MemoizedTable data={filteredServices} {...rest} />
    </>
  );
};

const Form = styled.form`
  display: flex;
  margin-bottom: 20px;
  * + * {
    margin-left: 10px;
  }
`;

const Content = ({}) => {
  const servicesStore = useServicesStore();
  servicesStore.initServicesData();

  switch (servicesStore.servicesData.type) {
    case 'initial':
    case 'loading':
      return <Loader text="Loading services..." />;
    case 'errored':
      return <div>Error {servicesStore.servicesData.error.message}</div>;
    case 'loaded':
      return <ServiceListImpl data={servicesStore.servicesData.data} />;
  }

  const _exhaustiveCheck: never = servicesStore.servicesData;
};

export function ServicePage(props: { path: string }) {
  return (
    <PageContent header="Services">
      <Content />
    </PageContent>
  );
}
